/**
 * №29 (D-L1, S0) — THE SHELL: one shell, three zones, on every route.
 *
 *   LEFT   `ShellNav`      navigation, always — brand, search (⌘K), the six, you
 *                          (№53 D-CR12: the crew and RUNNING sections left the rail)
 *   CENTRE `<main.stage>`  the SUBJECT — cards, a table, a record, a run, a desk
 *   RIGHT  `WorkRail`      the crew's working state — Plan · Context · Receipts · Memory
 *
 * **The subject changes; the shell doesn't.** All three zones are grid children of
 * this one element and are mounted ONCE: only the stage's BODY is keyed by pathname
 * (so a route change resets its scroll and state), while the stage element itself,
 * the nav and the rail keep their identity across every navigation. That is D-SS2's
 * re-housed SPA discipline — 22.3's D-CH3 promoted from "the panel never remounts"
 * to "the SHELL never remounts" — and it is asserted permanently by
 * `tests/snapshots/shell-identity-29.spec.ts`.
 *
 * The geometry itself is DECLARED, not inferred: `resolveSubject(pathname)` reads
 * the same matrix row as the anchor resolver, so the stage's subject and the rail's
 * bound context come from one place and cannot drift (see route-anchor-matrix.ts).
 * A row declaring `rail: "none"` renders the probe's two-zone variant (mockup C).
 *
 * **What retired here (§3/§4, listed in the S0 findings).** The №14.1 `.topbar` is
 * gone: its identity lockup moved to the head of the nav column, its right-hand
 * controls to the foot, and its non-interactive "Search ledgers…" `<div>` — chrome
 * that never did anything — is deleted rather than re-drawn. The two-axis rail is
 * now one column with sections.
 *
 * Entry is still gated on `ledger_enabled`; session/membership is gated by the
 * enclosing AuthGuard.
 */
import { type CSSProperties, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import "../../styles/tokens.css";
import "./shell-themes.css";
import "./app-chrome.css";
import { useWorkspaceInfo } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { useAppSitemap } from "../../hooks/use-app-sitemap";
import { SYSTEM_FALLBACK_PLACES } from "./sitemap";
import { workbenchNavRoute } from "./workbench-registry";
import { conversationIsSubject, conversationRouteFor, resolveSubject, subjectRouteFor, type RailMode } from "./route-anchor-matrix";
import { ToastContainer } from "../../components/primitives";
import { BackBar } from "./BackBar";
import { ShellNav } from "./ShellNav";
import { SubjectHeader } from "./SubjectHeader";
import { RecordTabs } from "./RecordTabs";
import { AppStrip } from "./AppStrip";
import { WorkRail } from "../../components/shell/WorkRail";
import { useCoPanel } from "../../components/collab/co-panel-context";
import { useShellIdentity } from "../../hooks/use-shell-identity";
import { useCommandPalette } from "../../components/command-palette/CommandPaletteContext";
import { AppCommandPalette } from "./AppCommandPalette";

/**
 * №53 · D-CR30 — THE RIGHT RAIL IS OFF, AND IT IS NOT DELETED.
 *
 * David, 2026-08-20: *"I also want to reconsider the chat window — starting by
 * removing it. Can you remove the chat bar from the right side. We need to
 * reimagine how we interact with the system."* Asked how deep to cut, he ruled:
 * *"Whole rail goes. Don't delete the component. Just remove it for now."*
 *
 * So the whole right zone goes dark — Chat, Plan, Context, Receipts, Memory and
 * the composer — and every `/app` surface renders two-zone. **This is a deliberate
 * exception to "removals are deliverables"** (console-conventions.md): that rule
 * exists so a spec's kill list cannot be quietly satisfied by hiding, and here the
 * hiding IS the instruction — the interaction model is being redesigned and the
 * rail is expected to come back in some form. The exception is written down rather
 * than assumed, which is the difference between a parked decision and a leak.
 *
 * **Why a forced `mode="none"` rather than dropping the rail element.**
 * `WorkRail` is the ONLY component that portals the CoPanel into the conversation
 * host, and `mode="none"` is a shipped path that renders nothing visible while
 * keeping that portal alive (WorkRail.tsx). Unmounting the element instead would
 * ① blank the `/app/**\/chat` subject routes, which re-parent that same host onto
 * the stage, ② break the shell's mount-identity invariant that
 * `shell-identity-29.spec.ts` asserts, and ③ trip `spa-nav-gate.mjs`, which
 * requires `CoPanel` to be rendered in the shell. One flag, no component deleted,
 * no gate weakened: flip this back to `true` and the rail returns exactly as it was.
 */
const RIGHT_RAIL_ON = false;

export function AppShellLayout() {
  const { user, currentWorkspaceId } = useAuth();
  const wsInfo = useWorkspaceInfo();
  const location = useLocation();
  // The right zone's open state drives the shell's third grid track. Called before
  // any early return so hook order is stable (React #310).
  const rightZone = useCoPanel();
  const setRailExpanded = rightZone.setExpanded;
  useShellIdentity("shell");
  useShellIdentity("stage");
  // §5 — the narrow nav drawer. One boolean; the CSS decides whether it means
  // anything (above the breakpoint the nav is always in the grid).
  const [navOpen, setNavOpen] = useState(false);
  // №44 — ⌘K. Called before any early return so hook order is stable (React #310).
  const palette = useCommandPalette();
  const paletteOpen = palette.open;
  const setPaletteOpen = palette.setOpen;

  const workspace = user?.workspaces.find((w) => w.id === currentWorkspaceId);
  const ledgerEnabled = wsInfo.data?.ledger_enabled;

  // Spec №14 (D-SH1) — the workspace shell. `theme` drives the token overlay
  // (D-SS1's new violet base re-maps per workspace here); `brand` drives the
  // wordmark + page title. Null when the workspace declares no shell.
  const shell = wsInfo.data?.shell ?? null;
  const brandName = shell?.brand?.name ?? null;

  // Spec №11 (D-M4) — the Places axis is assembled server-side.
  const sitemap = useAppSitemap();

  // №29 (D-SS2) — the SUBJECT ROUTER. One read, above the outlet: what the stage
  // holds, and whether the work rail is mounted beside it.
  const subject = useMemo(() => resolveSubject(location.pathname), [location.pathname]);
  // №29 (S1) — where the conversation goes when the user makes it the subject, and
  // where the subject goes when they hand it back. Both derived from the ONE matrix
  // (via `promotedFrom`), so neither control can point at a different anchor; null
  // means this surface declares no conversation row, and then there is no control.
  const conversationRoute = useMemo(() => conversationRouteFor(location.pathname), [location.pathname]);
  const subjectRoute = useMemo(() => subjectRouteFor(location.pathname), [location.pathname]);
  // Navigating closes the drawer — otherwise it sits open over the surface you just
  // asked for. The React-blessed "store information from previous renders" pattern
  // (setState during render, no effect, no cascade), as used by the panel provider.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [navPath, setNavPath] = useState(location.pathname);
  if (navPath !== location.pathname) {
    setNavPath(location.pathname);
    if (navOpen) setNavOpen(false);
  }

  // Spec №14 (14.3) — brand the page title + favicon from the shell. Runs before
  // any early return so hook order is stable (React #310). Restores on unmount.
  useEffect(() => {
    if (!brandName) return;
    const prevTitle = document.title;
    document.title = workspace?.name ? `${workspace.name} · ${brandName}` : brandName;
    let favEl: HTMLLinkElement | null = null;
    let prevFav: string | null = null;
    if (shell?.brand?.favicon) {
      favEl = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (favEl) {
        prevFav = favEl.getAttribute("href");
        favEl.setAttribute("href", shell.brand.favicon);
      }
    }
    return () => {
      document.title = prevTitle;
      if (favEl && prevFav !== null) favEl.setAttribute("href", prevFav);
    };
  }, [brandName, workspace?.name, shell?.brand?.favicon]);

  // §5 — at narrow the rail's DEFAULT is collapsed: it becomes an overlay there, and
  // an overlay covering the surface you just navigated to is not a default, it is an
  // obstruction. This is a genuine external-system subscription (the viewport), so it
  // is an effect; the user can still open the rail from its edge tab, and crossing
  // back to a wide viewport restores their own preference.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const apply = (narrow: boolean) => { if (narrow) setRailExpanded(false); };
    apply(mq.matches);
    const onChange = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [setRailExpanded]);

  // №44 — ⌘/Ctrl+K opens the palette from anywhere in `/app`, whatever has focus.
  //
  // Two guards, both carried over from the console's handler (`AppShell.tsx:51–93`)
  // because both were earned there: it never opens ON TOP of another dialog (modal
  // isolation — stealing focus from an open modal is how a keyboard shortcut becomes
  // a bug), and it toggles rather than re-opening, so ⌘K twice is a no-op rather
  // than a stuck overlay. Unlike the console's, this binds ⌘K ONLY: ⌘/ opens a
  // cheatsheet of shortcuts, and `/app` has exactly one to teach.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key !== "k" && e.key !== "K") return;
      e.preventDefault();
      if (paletteOpen) {
        setPaletteOpen(false);
      } else if (!document.querySelector('[role="dialog"]')) {
        setPaletteOpen(true);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [paletteOpen, setPaletteOpen]);

  // Flag gate: only redirect once workspace-info has resolved (avoid a flash).
  if (wsInfo.isSuccess && ledgerEnabled === false) {
    return <Navigate to="/" replace />;
  }

  const places = (sitemap.data?.places ?? SYSTEM_FALLBACK_PLACES).filter((e) => e.axis === "places");

  // The set of TOP-LEVEL nav destinations (`/app` + every Place's route), derived
  // from the same sitemap the nav renders. The universal Back button shows on any
  // route NOT in this set (i.e. a drill-down). See BackBar.
  const topLevelPaths = new Set<string>(["/app"]);
  for (const e of places) {
    const target = e.route ?? (e.workbench ? workbenchNavRoute(e.workbench) : undefined);
    if (target) topLevelPaths.add(target.replace(/\/+$/, ""));
  }

  // The two-zone variant (mockup C): a declared `rail: "none"` surface, or a narrow
  // viewport, where the rail collapses behind its edge tab. `--workrail-w` is the
  // third track; 0 closes it without unmounting anything.
  const railOpen = RIGHT_RAIL_ON && subject.rail === "work" && rightZone.expanded;
  // Every surface is two-zone while the rail is off — the attribute is what the
  // shell gates and the CI journeys read, so it must tell the truth about what
  // rendered, not about what the matrix declared.
  const railMode: RailMode = RIGHT_RAIL_ON ? subject.rail : "none";

  return (
    // №53 · 53.0.1 (D-CR9, audit item 7) — THE GROUND BEHIND THE SHEET. `.appshell`
    // below is now capped at 1440px (app-chrome.css); this wrapper paints the
    // gutters that appear at wider viewports in crew's own page-ground colour
    // (see the CSS rule's own comment for why it cannot live on `.appshell` or on
    // the global `body` rule). Adds no grid track, no test hook, no state — a
    // single static div around the shell that already existed.
    <div className="appground">
      <div
        className="appshell"
        data-v3
        data-shell-theme={shell?.theme ?? undefined}
        // №29 — the shell declares what it is holding, so a CI journey (and a reader
        // of the DOM) can see the subject router's decision without inferring it.
        data-subject={subject.subject}
        data-zones={railMode === "none" ? "two" : "three"}
        data-surface={subject.key}
        data-nav={navOpen ? "open" : undefined}
        style={{ ["--workrail-w" as string]: railOpen ? "408px" : "0px" } as CSSProperties}
      >
        {/* LEFT — navigation. Mounted once; never keyed. */}
        <ShellNav brandName={brandName} places={places} onOpenPalette={() => setPaletteOpen(true)} />
        {/* Narrow only: the drawer's scrim. Above the breakpoint the CSS never shows
            it, so it costs nothing on the desktop geometry. */}
        {navOpen && (
          <button
            type="button"
            className="shellnav__scrim"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
            data-testid="shellnav-scrim"
          />
        )}

        {/* CENTRE — the subject. The ELEMENT is stable across navigation; only its
            body is keyed, so scroll/state reset without the shell blinking. */}
        <main className="stage" data-testid="stage">
          <SubjectHeader subject={subject.subject} onOpenNav={() => setNavOpen(true)} subjectRoute={subjectRoute} />
          <div className="stage__body" key={location.pathname} data-testid="stage-body">
            {/* №53 · 53.0.1 (D-CR2) — THE STRIP FRAME. Mounted FIRST, ahead of the
                back-bar and the record tabs: crew's `.strip` is the first thing
                inside `.main`, sticky at the top of the ONE scroll region
                (`.stage__body` itself, `overflow-y: auto` — see the alias rule
                above), so it must sit first in DOM order for that stickiness to
                read the way the drawing draws it, regardless of what renders below
                it on any given route. A shell element, not a per-page one — every
                `/app` route carries it. */}
            <AppStrip />
            {/* №29 (S1) — not on a chat subject. The stage head already carries an
                explicit, better-named way back ("Move to the rail"), and a generic
                "Back" above a conversation is a second answer to the same question. */}
            {!conversationIsSubject(subject.subject) && <BackBar topLevel={topLevelPaths} />}
            {/* №48 — a record's sub-views, as tabs on the record. Mounted here and
                only here: each sub-view is its own route, so a per-page mount is the
                same strip pasted into every record component and one omission loses
                the navigation on exactly one tab. Renders nothing off the record
                family. */}
            <RecordTabs />
            <Outlet />
          </div>
        </main>

        {/* RIGHT — the work rail. A grid sibling of <main> (NOT inside it), so the
            keyed body above never touches it: mounted once, survives all navigation
            (D-SS2). On a two-zone surface it renders nothing VISIBLE but stays
            mounted — unmounting it would break the identity invariant the shell gate
            asserts, and would drop an in-flight conversation on the way to intake. */}
        <WorkRail subject={subject.subject} mode={railMode} conversationRoute={conversationRoute} />

        {/* №29 (§3 ②, S3) — the bottom bar launcher is DELETED. It existed to start a
            conversation while the right zone was shut; the rail's own composer is now
            the only one, and a second composer pinned over the first is the duplication
            this removal ends. Its live badge did not vanish with it — it moved to the
            left nav's "Needs you" row, which is where the count already had a slot. */}

        {/* №44 — ⌘K. Mounted here rather than inside the stage so it survives every
            navigation, like the rail; it renders nothing until first opened. */}
        <AppCommandPalette />

        <ToastContainer />
      </div>
    </div>
  );
}
